/**
 * Created by razial on 19.01.2026.
 */

const DataVersion = function () {
    this.appVersion = cleverapps.config.version;
    this.prevAppVersion = undefined;

    this.dataVersion = 0;
    this.minDataVersion = 0;

    this.clientDataVersion = 0;
    this.clientMinDataVersion = DataVersion.MIN_DATA_COMPATIBLE_VERSION;

    this.migrations = [];
    this.backMigrations = [];

    const usedVersions = new Set();

    for (const factory of DataVersion.MIGRATIONS) {
        const migration = factory();

        if (!Number.isFinite(migration.version)) {
            throw new Error(`migration has invalid version ${migration.version}`);
        }

        if (usedVersions.has(migration.version)) {
            throw new Error(`duplicate migration version ${migration.version}`);
        }

        usedVersions.add(migration.version);

        this.migrations.push(migration);

        if (migration.version > this.clientDataVersion) {
            this.clientDataVersion = migration.version;
        }
    }

    this.migrations.sort((a, b) => a.version - b.version);

    for (const factory of DataVersion.BACK_MIGRATIONS) {
        const migration = factory();

        if (!Number.isFinite(migration.toVersion)) {
            throw new Error(`back migration has invalid version ${migration.toVersion}`);
        }

        this.backMigrations.push(migration);
    }

    this.backMigrations.sort((a, b) => a.toVersion - b.toVersion).reverse();
};

DataVersion.prototype.load = function () {
    let data = cleverapps.dataLoader.load(DataLoader.TYPES.DATA_VERSION);

    const isNewUser = cleverapps.dataLoader.haveSavedData() === false;

    if (isNewUser) {
        data = {
            dataversion: this.clientDataVersion,
            mindataversion: this.clientMinDataVersion
        };
    } else {
        const userData = cleverapps.dataLoader.load(DataLoader.TYPES.ID);
        if (!userData || !userData.version || Version.compare(userData.version, "1.473.3") <= 0) {
            data = {};
        }
    }

    this.updateInfo(data);

    if (isNewUser) {
        this.save();
    }
};

DataVersion.prototype.save = function () {
    cleverapps.dataLoader.save(DataLoader.TYPES.DATA_VERSION, this.getInfo());
};

DataVersion.prototype.updateInfo = function (serverData, fromServer) {
    serverData = serverData || {};

    this.prevAppVersion = serverData.appversion;

    this.dataVersion = serverData.dataversion || 0;
    this.minDataVersion = serverData.mindataversion || 0;

    if (fromServer) {
        this.save();
    }
};

DataVersion.prototype.getInfo = function () {
    return {
        appversion: this.appVersion,
        dataversion: this.dataVersion || undefined,
        mindataversion: this.minDataVersion || undefined
    };
};

DataVersion.prototype.listMigrations = function () {
    return this.migrations;
};

DataVersion.prototype.listBackMigrations = function () {
    return this.backMigrations;
};

DataVersion.prototype.isClientCompatible = function () {
    return this.clientDataVersion >= this.minDataVersion;
};

DataVersion.prototype.onLoad = function () {
    delete this.useSingleLocationMigrationNotice;
    delete this.hardConversionData;

    this.load();

    console.log(`data version loaded ${this.clientDataVersion} ${this.minDataVersion} ${this.dataVersion}`);

    if (this.prevAppVersion && this.appVersion && Version.compare(this.prevAppVersion, this.appVersion) > 0) {
        cleverapps.metrics.logEvent(`${cleverapps.EVENTS.DEBUG.VERSION_DOWNGRADE}-${this.prevAppVersion}-${this.appVersion}`);
    }

    if (!this.isClientCompatible()) {
        console.log("data version incompatible");

        connector.dataLoader.setEnabled(false);
        cleverapps.dataLoader.setEnabled(false);
        connector.synchronizer.disable();

        cleverapps.metrics.logEvent(`${cleverapps.EVENTS.MIGRATION_INCOMPATIBLE}-onload-${this.clientDataVersion}`);
        return;
    }

    if (this.prevAppVersion && this.appVersion && Version.compare(this.prevAppVersion, this.appVersion) > 0) {
        cleverapps.metrics.logEvent(`${cleverapps.EVENTS.CLIENT_DOWNGRADE}-${this.prevAppVersion}-${this.appVersion}`);
    }

    try {
        for (const migration of this.listMigrations()) {
            if (migration.version > this.dataVersion) {
                console.log("data version load - run migration", migration.version);
                migration.migrateOnLoad(this);

                cleverapps.metrics.logEvent(`${cleverapps.EVENTS.MIGRATION}-onload-${migration.version}`);
            }
        }

        for (const migration of this.listBackMigrations()) {
            if (migration.toVersion < this.dataVersion && migration.toVersion >= this.clientDataVersion) {
                console.log("data version load - run back migration", migration.toVersion);
                migration.migrateBackOnLoad(this);

                cleverapps.metrics.logEvent(`${cleverapps.EVENTS.MIGRATION_BACK}-onload-${migration.toVersion}`);
            }
        }
    } catch (e) {
        connector.dataLoader.setEnabled(false);
        cleverapps.dataLoader.setEnabled(false);
        connector.synchronizer.disable();
        throw e;
    }

    if (this.dataVersion !== this.clientDataVersion || this.minDataVersion !== this.clientMinDataVersion) {
        this.dataVersion = this.clientDataVersion;
        this.minDataVersion = this.clientMinDataVersion;
        this.save();

        connector.synchronizer.syncOut();
    } else if (this.appVersion !== this.prevAppVersion) {
        this.save();

        connector.synchronizer.addSendTask("dataversion");
    }
};

DataVersion.prototype.onSync = function (snapshot) {
    delete this.useSingleLocationMigrationNotice;
    delete this.hardConversionData;

    this.updateInfo(snapshot.dataversion, true);

    console.log(`data version synced ${this.clientDataVersion} ${this.minDataVersion} ${this.dataVersion}`);

    if (!this.isClientCompatible()) {
        console.log("data version incompatible");

        connector.dataLoader.setEnabled(false);
        cleverapps.dataLoader.setEnabled(false);
        connector.synchronizer.disable();

        cleverapps.metrics.logEvent(`${cleverapps.EVENTS.MIGRATION_INCOMPATIBLE}-onsync-${this.clientDataVersion}`);
        return;
    }

    if (this.prevAppVersion && this.appVersion && Version.compare(this.prevAppVersion, this.appVersion) > 0) {
        cleverapps.metrics.logEvent(`${cleverapps.EVENTS.SERVER_DOWNGRADE}-${this.prevAppVersion}-${this.appVersion}`);
    }

    try {
        for (const migration of this.listMigrations()) {
            if (migration.version > this.dataVersion) {
                console.log("data version sync - run migration", migration.version);
                migration.migrateOnSync(snapshot, this);

                cleverapps.metrics.logEvent(`${cleverapps.EVENTS.MIGRATION}-onsync-${migration.version}`);
            }
        }

        for (const migration of this.listBackMigrations()) {
            if (migration.toVersion < this.dataVersion && migration.toVersion >= this.clientDataVersion) {
                console.log("data version sync - run back migration", migration.toVersion);
                migration.migrateBackOnSync(snapshot, this);

                cleverapps.metrics.logEvent(`${cleverapps.EVENTS.MIGRATION_BACK}-onsync-${migration.toVersion}`);
            }
        }
    } catch (e) {
        connector.dataLoader.setEnabled(false);
        cleverapps.dataLoader.setEnabled(false);
        connector.synchronizer.disable();
        throw e;
    }

    if (this.dataVersion !== this.clientDataVersion || this.minDataVersion !== this.clientMinDataVersion) {
        this.dataVersion = this.clientDataVersion;
        this.minDataVersion = this.clientMinDataVersion;
        this.save();

        connector.synchronizer.syncOut();
    } else if (this.appVersion !== this.prevAppVersion) {
        this.save();

        connector.synchronizer.addSendTask("dataversion");
    }
};

DataVersion.prototype.displayUpgradeWindow = function (f) {
    if (this.isClientCompatible()) {
        f();
    } else {
        cleverapps.newVersionChecker.showUpgradeWindow(f, NewVersionChecker.TRIGGER_INCOMPATIBLE_DATA);
    }
};

DataVersion.MIN_DATA_COMPATIBLE_VERSION = 0;

DataVersion.MIGRATIONS = new Set();
DataVersion.BACK_MIGRATIONS = new Set();

DataVersion.registerSoftToHardConversion = function (options) {
    const forward = options.forward || [];
    const back = options.back || [];

    if (back.includes(cleverapps.config.name)) {
        DataVersion.BACK_MIGRATIONS.add(() => ({
            toVersion: 13,

            migrateBackOnLoad: function () {
                const userData = cleverapps.dataLoader.load(DataLoader.TYPES.ID);
                if (userData) {
                    userData.gold = Math.floor((userData.gold || 0) / 7);
                    cleverapps.dataLoader.save(DataLoader.TYPES.ID, userData);
                }
            },

            migrateBackOnSync: function (snapshot) {
                if (snapshot.users) {
                    snapshot.users.gold = Math.floor((snapshot.users.gold || 0) / 7);
                }
            }
        }));
    }

    if (forward.includes(cleverapps.config.name)) {
        DataVersion.MIGRATIONS.add(() => ({
            version: 14, // tmp version, change to actual one when removing from debug mode

            migrateOnLoad: function (migrator) {
                const userData = cleverapps.dataLoader.load(DataLoader.TYPES.ID);
                if (userData) {
                    migrator.hardConversionData = { hard: userData.gold || 0, soft: userData.soft || 0 };
                    userData.gold = (userData.gold || 0) * 7 + Math.floor((userData.soft || 0) * 7 / 5);
                    userData.soft = 0;
                    cleverapps.dataLoader.save(DataLoader.TYPES.ID, userData);
                }
            },

            migrateOnSync: function (snapshot, migrator) {
                if (snapshot.users) {
                    migrator.hardConversionData = { hard: snapshot.users.gold || 0, soft: snapshot.users.soft || 0 };
                    snapshot.users.gold = (snapshot.users.gold || 0) * 7 + Math.floor((snapshot.users.soft || 0) * 7 / 5);
                    snapshot.users.soft = 0;
                }
            }
        }));
    }
};
